import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, List, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Modal, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/FormCard';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import type { StatusKey } from '@/types';
import { toast } from '@/store/toastStore';
import { listUtilisateurs } from '@/services/parametres';
import { listChantiers } from '@/services/chantiers';
import {
  type PrioriteTache, type StatutTache, type Tache, type TacheFiltres, type TacheInput,
  PRIORITES_TACHE, PRIORITE_TACHE_LABEL, STATUTS_TACHE, STATUT_TACHE_LABEL,
  changerStatutTache, createTache, deleteTache, listTaches, updateTache,
} from '@/services/taches';

const selectCls = 'flex h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm';
const PRIO_UI: Record<PrioriteTache, StatusKey> = { BASSE: 'done', NORMALE: 'info', HAUTE: 'stuck' };
const STATUT_UI: Record<StatutTache, StatusKey> = { A_FAIRE: 'pending', EN_COURS: 'working', EN_REVISION: 'info', TERMINE: 'done' };
const vide = (): TacheInput => ({ titre: '', description: '', chantier_id: '', responsable_id: '', priorite: 'NORMALE', statut: 'A_FAIRE', date_debut: '', date_echeance: '' });

export function TachesPage() {
  const qc = useQueryClient();
  const [vue, setVue] = useState('liste');
  const [filtres, setFiltres] = useState<TacheFiltres>({});
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<TacheInput>(vide());
  const setF = (patch: Partial<TacheFiltres>) => setFiltres((f) => ({ ...f, ...patch }));

  const { data: taches, isLoading } = useQuery({ queryKey: ['taches', filtres], queryFn: () => listTaches(filtres) });
  const { data: users } = useQuery({ queryKey: ['utilisateurs'], queryFn: listUtilisateurs });
  const { data: chantiersData } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers() });
  const chantiers = chantiersData?.items ?? [];
  const usersById = useMemo(() => Object.fromEntries((users ?? []).map((u) => [u.id, `${u.prenom} ${u.nom}`])), [users]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['taches'] }); qc.invalidateQueries({ queryKey: ['notifications'] }); };

  const saveMut = useMutation({
    mutationFn: () => {
      const payload: TacheInput = {
        titre: form.titre.trim(), description: form.description || null,
        chantier_id: form.chantier_id || null, responsable_id: form.responsable_id || null,
        priorite: form.priorite, statut: form.statut,
        date_debut: form.date_debut || null, date_echeance: form.date_echeance || null,
      };
      return editId ? updateTache(editId, payload) : createTache(payload);
    },
    onSuccess: () => { invalidate(); setOpen(false); setEditId(null); setForm(vide()); toast.success(editId ? 'Tâche modifiée.' : 'Tâche créée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const statutMut = useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: StatutTache }) => changerStatutTache(id, statut),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: deleteTache, onSuccess: () => { invalidate(); toast.success('Tâche supprimée.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const ouvrirEdition = (t: Tache) => {
    setEditId(t.id);
    setForm({ titre: t.titre, description: t.description ?? '', chantier_id: t.chantier_id ?? '', responsable_id: t.responsable_id ?? '', priorite: t.priorite, statut: t.statut, date_debut: t.date_debut ?? '', date_echeance: t.date_echeance ?? '' });
    setOpen(true);
  };
  const ouvrirCreation = () => { setEditId(null); setForm(vide()); setOpen(true); };

  const items = taches ?? [];

  return (
    <>
      <PageHeader title="Tâches" subtitle="Suivi transverse — assignation, priorité, statut (liste et kanban)"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Tâches' }]}
        actions={<Button onClick={ouvrirCreation}><Plus /> Nouvelle tâche</Button>} />

      {/* Filtres */}
      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
        <label className="flex flex-col text-xs text-content-muted">Projet
          <select className={selectCls} value={filtres.chantier_id ?? ''} onChange={(e) => setF({ chantier_id: e.target.value || null })}>
            <option value="">Tous</option>{chantiers.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Assigné à
          <select className={selectCls} value={filtres.responsable_id ?? ''} onChange={(e) => setF({ responsable_id: e.target.value || null })}>
            <option value="">Tous</option>{(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Priorité
          <select className={selectCls} value={filtres.priorite ?? ''} onChange={(e) => setF({ priorite: (e.target.value || null) as PrioriteTache | null })}>
            <option value="">Toutes</option>{PRIORITES_TACHE.map((p) => <option key={p} value={p}>{PRIORITE_TACHE_LABEL[p]}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Statut
          <select className={selectCls} value={filtres.statut ?? ''} onChange={(e) => setF({ statut: (e.target.value || null) as StatutTache | null })}>
            <option value="">Tous</option>{STATUTS_TACHE.map((s) => <option key={s} value={s}>{STATUT_TACHE_LABEL[s]}</option>)}
          </select>
        </label>
        {(filtres.chantier_id || filtres.responsable_id || filtres.priorite || filtres.statut) && (
          <Button variant="secondary" size="sm" onClick={() => setFiltres({})}>Réinitialiser</Button>
        )}
        <div className="ml-auto">
          <Tabs value={vue} onChange={setVue} tabs={[{ key: 'liste', label: 'Liste', icon: <List className="size-4" /> }, { key: 'kanban', label: 'Kanban', icon: <LayoutGrid className="size-4" /> }]} />
        </div>
      </div>

      {isLoading ? <Skeleton className="h-64 w-full" /> : vue === 'liste' ? (
        items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">Aucune tâche.</div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 label-caps">Tâche</th><th className="px-4 py-2.5 label-caps">Projet</th>
                <th className="px-4 py-2.5 label-caps">Assigné à</th><th className="px-4 py-2.5 label-caps">Échéance</th>
                <th className="px-4 py-2.5 label-caps">Priorité</th><th className="px-4 py-2.5 label-caps">Statut</th><th className="px-4 py-2.5" />
              </tr></thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                    <td className="px-4 py-2 font-medium text-content-primary">{t.titre}</td>
                    <td className="px-4 py-2 text-content-secondary">{t.chantier_label ?? <span className="text-content-muted">—</span>}</td>
                    <td className="px-4 py-2">{t.responsable_nom ?? <span className="text-content-muted">Non assigné</span>}</td>
                    <td className="px-4 py-2 tabular-nums text-content-secondary">{t.date_echeance ?? '—'}</td>
                    <td className="px-4 py-2"><StatusBadge status={PRIO_UI[t.priorite]} label={PRIORITE_TACHE_LABEL[t.priorite]} /></td>
                    <td className="px-4 py-2"><StatusBadge status={STATUT_UI[t.statut]} label={STATUT_TACHE_LABEL[t.statut]} /></td>
                    <td className="px-4 py-2"><div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => ouvrirEdition(t)} aria-label="Modifier"><Pencil className="size-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm('Supprimer cette tâche ?')) delMut.mutate(t.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {STATUTS_TACHE.map((col) => {
            const cards = items.filter((t) => t.statut === col);
            return (
              <div key={col} className="flex flex-col rounded-lg border border-border bg-surface-secondary"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { const id = e.dataTransfer.getData('id'); if (id) statutMut.mutate({ id, statut: col }); }}>
                <div className="flex items-center justify-between border-b border-border px-3 py-2">
                  <span className="text-sm font-semibold text-content-primary">{STATUT_TACHE_LABEL[col]}</span>
                  <span className="rounded-full bg-surface-hover px-2 text-xs tabular-nums text-content-muted">{cards.length}</span>
                </div>
                <div className="flex min-h-[120px] flex-col gap-2 p-2">
                  {cards.map((t) => (
                    <div key={t.id} draggable onDragStart={(e) => e.dataTransfer.setData('id', t.id)}
                      onClick={() => ouvrirEdition(t)}
                      className="cursor-grab rounded-md border border-border bg-surface-primary p-2.5 shadow-sm hover:border-brand-primary active:cursor-grabbing">
                      <div className="mb-1 flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-content-primary">{t.titre}</span>
                        <StatusBadge status={PRIO_UI[t.priorite]} label={PRIORITE_TACHE_LABEL[t.priorite]} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-content-muted">
                        {t.chantier_label && <span>{t.chantier_label}</span>}
                        <span>{t.responsable_nom ?? 'Non assigné'}</span>
                        {t.date_echeance && <span className="tabular-nums">éch. {t.date_echeance}</span>}
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && <p className="py-4 text-center text-xs text-content-muted">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Création / édition */}
      <Modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }} title={editId ? 'Modifier la tâche' : 'Nouvelle tâche'} size="lg"
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!form.titre.trim()}>Enregistrer</Button></>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Titre" className="sm:col-span-2"><Input value={form.titre} onChange={(e) => setForm((f) => ({ ...f, titre: e.target.value }))} placeholder="Ex. Couler la dalle du R+1" /></FormField>
          <FormField label="Description" className="sm:col-span-2"><textarea className={cn(selectCls, 'h-20 py-2')} value={form.description ?? ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></FormField>
          <FormField label="Projet (optionnel)"><select className={selectCls} value={form.chantier_id ?? ''} onChange={(e) => setForm((f) => ({ ...f, chantier_id: e.target.value }))}><option value="">— Aucun (transverse) —</option>{chantiers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}</select></FormField>
          <FormField label="Assigné à"><select className={selectCls} value={form.responsable_id ?? ''} onChange={(e) => setForm((f) => ({ ...f, responsable_id: e.target.value }))}><option value="">— Non assigné —</option>{(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}</select></FormField>
          <FormField label="Priorité"><select className={selectCls} value={form.priorite} onChange={(e) => setForm((f) => ({ ...f, priorite: e.target.value as PrioriteTache }))}>{PRIORITES_TACHE.map((p) => <option key={p} value={p}>{PRIORITE_TACHE_LABEL[p]}</option>)}</select></FormField>
          <FormField label="Statut"><select className={selectCls} value={form.statut} onChange={(e) => setForm((f) => ({ ...f, statut: e.target.value as StatutTache }))}>{STATUTS_TACHE.map((s) => <option key={s} value={s}>{STATUT_TACHE_LABEL[s]}</option>)}</select></FormField>
          <FormField label="Date de début"><Input type="date" value={form.date_debut ?? ''} onChange={(e) => setForm((f) => ({ ...f, date_debut: e.target.value }))} /></FormField>
          <FormField label="Échéance"><Input type="date" value={form.date_echeance ?? ''} onChange={(e) => setForm((f) => ({ ...f, date_echeance: e.target.value }))} /></FormField>
        </div>
        {usersById && Object.keys(usersById).length === 0 && <p className="mt-2 text-xs text-content-muted">Aucun utilisateur disponible pour l'assignation.</p>}
      </Modal>
    </>
  );
}
